import { escapeHTML } from '../../utils/escape.js';

/**
 * Staff "Reportes" section (openspec/changes/staff-reportes/spec.md B1): an
 * employee picks their name, types their PIN and a message for Caro. Pure
 * render; `js/reportes.js` owns state and listeners. The shell is painted once
 * (so typed text survives), and the employee options and the notice are
 * repainted in place.
 */

/**
 * @param {{ maxChars: number }} props
 * @returns {string}
 */
export function StaffReportesShell({ maxChars }) {
    return `
        <section data-reportes-staff class="max-w-2xl">
            <header class="mb-6">
                <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">Mensaje a Caro</h2>
                <p class="mt-1 text-sm text-text-muted">Propuestas, problemas, ideas o lo que quieras contarle. Lo firma tu nombre y tu PIN.</p>
            </header>
            <form data-action-form="reporte-enviar" novalidate
                class="bg-surface border border-border-subtle rounded-2xl p-5 sm:p-6 flex flex-col gap-5">
                <div class="grid gap-5 sm:grid-cols-[1fr_10rem]">
                    <label class="flex flex-col gap-2">
                        <span class="text-[11px] font-black uppercase tracking-widest text-text-muted">Tu nombre</span>
                        <select name="empleado_id" required data-reportes-empleados
                            class="w-full px-4 py-3 rounded-xl border border-border-subtle bg-base text-text-primary text-base">
                            ${StaffReportesOpciones({ status: 'loading', empleados: [] })}
                        </select>
                    </label>
                    <label class="flex flex-col gap-2">
                        <span class="text-[11px] font-black uppercase tracking-widest text-text-muted">PIN</span>
                        <input name="pin" type="password" inputmode="numeric" pattern="[0-9]{4}" maxlength="4"
                            autocomplete="off" required
                            class="w-full px-4 py-3 rounded-xl border border-border-subtle bg-base text-text-primary text-base tracking-[0.5em]">
                    </label>
                </div>
                <label class="flex flex-col gap-2">
                    <span class="text-[11px] font-black uppercase tracking-widest text-text-muted">Mensaje</span>
                    <textarea name="texto" rows="6" required
                        class="w-full px-4 py-3 rounded-xl border border-border-subtle bg-base text-text-primary text-base leading-relaxed resize-y"></textarea>
                    <span data-reportes-contador aria-live="polite" class="self-end text-[11px] font-bold tabular-nums text-text-muted">0 / ${maxChars}</span>
                </label>
                <div data-reportes-aviso></div>
                <button type="submit" data-reportes-enviar
                    class="self-start px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg disabled:opacity-60">
                    <i class="fa-solid fa-paper-plane mr-2" aria-hidden="true"></i>Enviar a Caro
                </button>
            </form>
        </section>
    `;
}

/**
 * Options of the employee select.
 * @param {{ status: 'loading' | 'error' | 'ready', empleados: Array<{ id: string, nombre: string }> }} props
 * @returns {string}
 */
export function StaffReportesOpciones({ status, empleados }) {
    if (status === 'loading') return '<option value="">Cargando nombres...</option>';
    if (status === 'error') return '<option value="">No se pudieron cargar los nombres</option>';
    const opciones = empleados.map((e) => `<option value="${escapeHTML(e.id)}">${escapeHTML(e.nombre)}</option>`).join('');
    return `<option value="">Elegí tu nombre</option>${opciones}`;
}

/**
 * Notice under the form: the result of a send, or why the names did not load.
 * @param {{ tipo: 'ok' | 'error', texto: string, reintentar?: boolean } | null} aviso
 * @returns {string}
 */
export function StaffReportesAviso(aviso) {
    if (!aviso) return '';
    const ok = aviso.tipo === 'ok';
    const clases = ok
        ? 'border-emerald-600/30 bg-emerald-600/10 text-emerald-800 dark:text-emerald-200'
        : 'border-red-600/30 bg-red-600/10 text-red-800 dark:text-red-200';
    const reintentar = aviso.reintentar
        ? '<button type="button" data-action="reportes-reintentar" class="ml-auto shrink-0 px-3 py-1.5 rounded-lg border border-current text-[11px] font-black uppercase tracking-widest">Reintentar</button>'
        : '';
    return `
        <div role="${ok ? 'status' : 'alert'}" class="flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-bold ${clases}">
            <i class="fa-solid ${ok ? 'fa-circle-check' : 'fa-circle-exclamation'}" aria-hidden="true"></i>
            <span>${escapeHTML(aviso.texto)}</span>
            ${reintentar}
        </div>
    `;
}
